"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useCallback, useEffect, useRef } from "react";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";

interface ProductShareButtonProps {
    url?: string;
    title?: string;
    translationPrefix?: string; // Örn: 'tour_detail', 'yacht_detail', 'yacht_route_detail'
    className?: string;
    /** true ise ikon yanında çevirisi doğru metin (Paylaş / Share) gösterilir; sağ bar menü satırı için */
    showLabel?: boolean;
}

export default function ProductShareButton({
    url,
    title,
    translationPrefix = 'share',
    className = '',
    showLabel = false,
}: ProductShareButtonProps) {
    const [showShareMenu, setShowShareMenu] = useState(false);
    const { getVariable } = useLanguageVariables();
    const menuRef = useRef<HTMLDivElement>(null);

    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    const shareTitle = title || (typeof window !== 'undefined' ? document.title : '');

    const t = useCallback((key: string, fallback: string): string => {
        const fullKey = translationPrefix ? `${translationPrefix}.${key}` : key;
        return getVariable(fullKey, fallback);
    }, [translationPrefix, getVariable]);

    const handleShare = useCallback((platform: string) => {
        const encodedUrl = encodeURIComponent(shareUrl);
        const encodedTitle = encodeURIComponent(shareTitle);
        
        const shareLinks: Record<string, string> = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
            whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
            email: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
        };

        if (platform === 'copy') {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(shareUrl).then(() => {
                    // Başarılı - opsiyonel: toast notification gösterilebilir
                    setShowShareMenu(false);
                }).catch((err) => {
                    console.error('Failed to copy:', err);
                    // Fallback: eski yöntem
                    const textArea = document.createElement('textarea');
                    textArea.value = shareUrl;
                    textArea.style.position = 'fixed';
                    textArea.style.opacity = '0';
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                        document.execCommand('copy');
                        setShowShareMenu(false);
                    } catch (fallbackErr) {
                        console.error('Fallback copy failed:', fallbackErr);
                    }
                    document.body.removeChild(textArea);
                });
            } else {
                // Fallback: eski yöntem
                const textArea = document.createElement('textarea');
                textArea.value = shareUrl;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand('copy');
                    setShowShareMenu(false);
                } catch (err) {
                    console.error('Copy failed:', err);
                }
                document.body.removeChild(textArea);
            }
            return;
        }

        if (shareLinks[platform]) {
            // Email için mailto linki - direkt açılır
            if (platform === 'email') {
                const link = document.createElement('a');
                link.href = shareLinks[platform];
                link.click();
            } else {
                // Diğer platformlar için popup pencere
                window.open(
                    shareLinks[platform], 
                    '_blank', 
                    'width=600,height=400,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no'
                );
            }
        }
        setShowShareMenu(false);
    }, [shareUrl, shareTitle]);

    // Dışarı tıklandığında menüyü kapat
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowShareMenu(false);
            }
        };

        if (showShareMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showShareMenu]);

    const shareLabel = t('share', 'Paylaş');

    return (
        <div className={`relative ${className}`} ref={menuRef}>
            <motion.button
                type="button"
                whileHover={{ scale: showLabel ? 1 : 1.1 }}
                whileTap={{ scale: showLabel ? 1 : 0.9 }}
                onClick={() => setShowShareMenu(!showShareMenu)}
                className={
                    showLabel
                        ? 'w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-alsero-dark-gray bg-gray-100 hover:bg-gray-200 transition-colors text-left'
                        : 'p-3 rounded-full bg-white text-alsero-first shadow-lg hover:bg-gray-50 transition-colors'
                }
                title={shareLabel}
                aria-label={shareLabel}
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className={showLabel ? 'w-4 h-4 shrink-0 text-alsero-first' : 'w-6 h-6'} viewBox="0 0 32 36.766">
                    <path d="M25.982,24.73a6,6,0,0,0-4.45,1.971l-9.908-6.136a5.995,5.995,0,0,0,0-4.364l9.908-6.137A6.017,6.017,0,1,0,20.375,8.2l-9.907,6.136a6.018,6.018,0,1,0,0,8.094l9.907,6.136a6.017,6.017,0,1,0,5.607-3.836ZM22.159,6.018a3.824,3.824,0,1,1,3.824,3.824A3.828,3.828,0,0,1,22.159,6.018ZM6.018,22.207a3.824,3.824,0,1,1,3.823-3.824A3.828,3.828,0,0,1,6.018,22.207Zm16.141,8.542a3.824,3.824,0,1,1,3.824,3.824A3.828,3.828,0,0,1,22.159,30.748Zm0,0"/>
                </svg>
                {showLabel && <span>{shareLabel}</span>}
            </motion.button>

            <AnimatePresence>
                {showShareMenu && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl p-2 min-w-[200px] z-50"
                    >
                        <button
                            type="button"
                            onClick={() => handleShare('facebook')}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                            aria-label={t('share.facebook', 'Facebook')}
                        >
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            <span className="text-alsero-dark-gray">{t('share.facebook', 'Facebook')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleShare('twitter')}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                            aria-label={t('share.twitter', 'Twitter')}
                        >
                            <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.224.084 4.907 4.907 0 004.594 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                            </svg>
                            <span className="text-alsero-dark-gray">{t('share.twitter', 'Twitter')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleShare('whatsapp')}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                            aria-label={t('share.whatsapp', 'WhatsApp')}
                        >
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            <span className="text-alsero-dark-gray">{t('share.whatsapp', 'WhatsApp')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleShare('email')}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                            aria-label={t('share.email', 'E-posta')}
                        >
                            <svg className="w-5 h-5 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="text-alsero-dark-gray">{t('share.email', 'E-posta')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleShare('copy')}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                            aria-label={t('share.copy_link', 'Linki Kopyala')}
                        >
                            <svg className="w-5 h-5 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span className="text-alsero-dark-gray">{t('share.copy_link', 'Linki Kopyala')}</span>
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

